import { Add01Icon } from "@hugeicons/core-free-icons";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { EnvironmentBadge, Tag } from "@/components/common/badges";
import { CodePanel, tok } from "@/components/common/code";
import { Icon } from "@/components/common/icon";
import { Page, PageTitle, Panel } from "@/components/common/page";
import { EmptyState, PageSkeleton } from "@/components/common/states";
import { Button } from "@/components/ui/button";
import { client } from "@/data";
import { qForProject } from "@/data/queries";
import { formatDateTime, relativeTime } from "@/lib/format";

export const Route = createFileRoute("/p/$project/keys")({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(qForProject(params.project).apiKeys()),
  pendingComponent: PageSkeleton,
  component: KeysPage,
});

function KeysPage() {
  const q = qForProject(Route.useParams().project);
  const { data: keys } = useSuspenseQuery(q.apiKeys());
  const live = keys.filter((key) => !key.revokedAt);
  const revoked = keys.filter((key) => key.revokedAt);

  return (
    <Page>
      <PageTitle
        title="API keys"
        description="One key per environment and per job. A key can be scoped to a subset of the API, so a pilot or a partner never gets write access by accident."
        actions={
          <Button size="sm" disabled>
            <Icon icon={Add01Icon} className="size-3.5" />
            Create key
          </Button>
        }
      />

      <Panel title="Active keys" bodyClassName="divide-y">
        {live.length === 0 ? (
          <EmptyState
            title="No active keys"
            description="A key is how your backend calls Aux0. Create one per environment and per job, so revoking one never takes the others down."
          />
        ) : (
          live.map((key) => (
            <div key={key.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[13px] font-medium">{key.name}</span>
                  <EnvironmentBadge environment={key.environment} />
                  {key.scopes ? (
                    <Tag
                      className="border-brand/30 bg-brand-soft/60 text-brand"
                      title={key.scopes.join(", ")}
                    >
                      {key.scopes.length} scopes
                    </Tag>
                  ) : null}
                </div>
                <p className="mt-0.5 font-mono text-[11.5px] text-ink-faint">
                  {key.prefix}••••••••••••••••
                </p>
              </div>
              <div className="text-right text-[11.5px] text-muted-foreground">
                <p>
                  Last used{" "}
                  {client.kind === "fixtures"
                    ? relativeTime(key.lastUsedAt)
                    : key.lastUsedAt
                      ? formatDateTime(key.lastUsedAt)
                      : "Never"}
                </p>
                <p className="text-ink-faint">
                  Created {formatDateTime(key.createdAt)} by{" "}
                  {key.createdBy ?? "Unknown"}
                </p>
              </div>
              <Button variant="ghost" size="sm" className="text-stop" disabled>
                Revoke
              </Button>
            </div>
          ))
        )}
      </Panel>

      <Panel
        title="Use a key"
        description="Every request is a bearer token. There is no second credential."
        bodyClassName="p-0"
      >
        <CodePanel
          title="request"
          copy={`curl https://api.aux0.com/v1/messages \\\n  -H "Authorization: Bearer aux0_live_7fd2..."`}
          className="rounded-none border-0"
        >
          <span>
            <span className={tok.kw}>curl</span> https://api.aux0.com/v1/messages{" "}
            <span className={tok.dim}>\</span>
            {"\n  "}
            <span className={tok.dim}>-H</span>{" "}
            <span className={tok.str}>"Authorization: Bearer aux0_live_7fd2..."</span>
          </span>
        </CodePanel>
      </Panel>

      {revoked.length > 0 ? (
        <Panel
          title="Revoked"
          description="Kept so the audit log can still name them"
          bodyClassName="divide-y"
        >
          {revoked.map((key) => (
            <div
              key={key.id}
              className="flex items-center gap-3 px-4 py-2.5 text-muted-foreground"
            >
              <span className="min-w-0 flex-1 truncate text-[12.5px] line-through">
                {key.name}
              </span>
              <span className="font-mono text-[11px] text-ink-faint">{key.prefix}</span>
              <span className="text-[11px] text-ink-faint">
                Revoked {relativeTime(key.revokedAt)}
              </span>
            </div>
          ))}
        </Panel>
      ) : null}
    </Page>
  );
}
